import { createContext, useContext, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useStore } from 'react-redux';
import { message } from 'antd';
import { v4 as uuid } from 'uuid';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { DIAGNOSTICS_ENDPOINTS, DIAGNOSTICS_TIMEOUTS } from '@/features/diagnostics/config/endpoints';
import { checkReachability } from '@/features/diagnostics/services/ReachabilityService';
import { runVideoCallSetupCheck } from '@/features/diagnostics/services/VideoCallSetupService';
import { createEmptyReport } from '@/features/diagnostics/types';

const DiagnosticsContext = createContext(null);
// Keep the guard across provider remounts while an earlier SDK session tears down.
let pendingRunCompletion = null;

const postTelemetry = (url, payload) => {
  if (!url) return;
  fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => undefined);
};

export const DiagnosticsProvider = ({ children }) => {
  const currentUser = useSelector(getCurrentUser);
  const trans = useSelector(translator);
  const store = useStore();
  const scheduleApi = useSelector((state) => state.apis.scheduleApi);
  const videoCallApi = useSelector((state) => state.apis.videoCallApi);
  const activeRun = useRef(null);
  const mounted = useRef(false);
  const providerScope = useRef(null);
  const [report, setReport] = useState(createEmptyReport);
  const [isRunning, setIsRunning] = useState(Boolean(pendingRunCompletion));

  useEffect(() => {
    mounted.current = true;
    providerScope.current = {};
    let subscribed = true;
    if (pendingRunCompletion) {
      setIsRunning(true);
      pendingRunCompletion.then(() => {
        if (subscribed) setIsRunning(false);
      });
    }
    return () => {
      subscribed = false;
      mounted.current = false;
      activeRun.current?.abort();
    };
  }, [currentUser?.useruuid]);

  const runTest = useCallback(async () => {
    if (activeRun.current || pendingRunCompletion) return;
    if (store.getState().videoCall.videoCallModalVisible) {
      message.warning(trans('CALL_ALREADY_IN_PROGRESS'));
      return;
    }
    const controller = new AbortController();
    const runScope = providerScope.current;
    let videoCall;
    let cleanupWarningShown = false;
    const warnCancelledCleanup = (teardownFailed = false) => {
      if (
        controller.signal.aborted &&
        mounted.current &&
        providerScope.current === runScope &&
        !cleanupWarningShown &&
        (videoCall?.cleanupErrors.length > 0 || teardownFailed)
      ) {
        message.warning(trans('DIAGNOSTICS_CLEANUP_FAILED'));
        cleanupWarningShown = true;
      }
    };
    let finishRun;
    pendingRunCompletion = new Promise((resolve) => { finishRun = resolve; });
    const teardowns = [];
    activeRun.current = controller;
    const unsubscribe = store.subscribe(() => {
      if (store.getState().videoCall.videoCallModalVisible) controller.abort();
    });
    setIsRunning(true);
    try {
      const results = await Promise.allSettled([
        checkReachability({
          endpoints: DIAGNOSTICS_ENDPOINTS,
          timeoutMs: DIAGNOSTICS_TIMEOUTS.request,
        }),
        runVideoCallSetupCheck({
          scheduleApi,
          videoCallApi,
          currentUseruuid: currentUser?.useruuid,
          timeoutMs: DIAGNOSTICS_TIMEOUTS.videoCallStep,
          readinessTimeoutMs: DIAGNOSTICS_TIMEOUTS.readiness,
          signal: controller.signal,
          onTeardownStarted: (completion) => { teardowns.push(completion); },
        }),
      ]);
      if (results[1].status === 'fulfilled') videoCall = results[1].value;
      const failure = results.find((result) => result.status === 'rejected');
      if (failure) throw failure.reason;
      const reachability = results[0].value;
      if (controller.signal.aborted) return;

      const nextReport = {
        schemaVersion: '1.0',
        reportId: uuid(),
        generatedAt: new Date().toISOString(),
        account: {
          accountId: currentUser?.useruuid || null,
          userLabel: currentUser?.username || null,
        },
        environment: {
          browser: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        reachability,
        videoCall,
      };

      setReport(nextReport);
      postTelemetry(DIAGNOSTICS_ENDPOINTS.reportTelemetry, nextReport);
    } finally {
      unsubscribe();
      warnCancelledCleanup();
      const cleanupResults = await Promise.allSettled(teardowns);
      const teardownFailed = cleanupResults.some((result) => result.status === 'rejected');
      if (teardownFailed) {
        console.error('Chime diagnostic teardown failed');
      }
      warnCancelledCleanup(teardownFailed);
      activeRun.current = null;
      pendingRunCompletion = null;
      finishRun();
      if (mounted.current) setIsRunning(false);
    }
  }, [currentUser?.useruuid, currentUser?.username, scheduleApi, videoCallApi, store, trans]);

  const value = useMemo(
    () => ({ report, isRunning, runTest }),
    [report, isRunning, runTest]
  );

  return <DiagnosticsContext.Provider value={value}>{children}</DiagnosticsContext.Provider>;
};

DiagnosticsProvider.propTypes = {
  children: PropTypes.node,
};

export const useDiagnostics = () => {
  const context = useContext(DiagnosticsContext);
  if (!context) {
    throw new Error('useDiagnostics must be used within a DiagnosticsProvider');
  }
  return context;
};
